// HotX TRON 充值 Console API，注释时间：2026-09-03 CST。收款地址只随有效订单返回。
import { ControlPlaneApiError } from '@hotx-cdn/shared';
import { httpClient } from './client';
import type { ConsoleHttpClient } from './types';

export type RechargeAssetCode = 'USDT_TRC20' | 'TRX';
export type RechargeOrderStatus =
  | 'CREATED'
  | 'WAITING_PAYMENT'
  | 'PAYMENT_DETECTED'
  | 'CONFIRMING'
  | 'SUCCESS'
  | 'EXPIRED'
  | 'CANCELLED'
  | 'MANUAL_REVIEW'
  | 'FAILED';

export interface RechargeAssetOption {
  assetCode: RechargeAssetCode;
  displayName: string;
  network: 'TRON';
  enabled: boolean;
  minimumSuggestedAmount?: string;
  usdRate?: string;
  rateSource?: string;
  rateEffectiveAt?: string;
  rateUpdatedAt?: string;
  officialContractAddress?: string;
}

export interface RechargeConfig {
  accountStatus: string;
  rechargeEnabled: boolean;
  autoCreditEnabled: boolean;
  addressBindingEnabled: boolean;
  claimEnabled: boolean;
  maxBoundAddresses: number;
  orderExpireMinutes?: number;
  suffixProtectionMinutes?: number;
  maxActiveOrders?: number;
  providerAvailable?: boolean;
  supportRequired?: boolean;
  assets: RechargeAssetOption[];
}

export interface RechargeOrder {
  id: number;
  orderId: string;
  assetCode: RechargeAssetCode;
  network: 'TRON';
  status: RechargeOrderStatus;
  requestedAmount: string;
  expectedAmount: string;
  actualAmount?: string;
  creditedPoints?: string;
  receiveAddress?: string;
  paymentQrContent?: string;
  paymentQrType?: 'TOKENPOCKET_USDT_TRC20' | 'TRX_NATIVE';
  paymentWindowOpen?: boolean;
  paymentPaused?: boolean;
  providerPausedAt?: string;
  paymentSecondsRemaining?: number;
  supportRequired?: boolean;
  expiresAt: string;
  protectionExpiresAt?: string;
  txid?: string;
  confirmations?: number;
  requiredConfirmations?: number;
  depositStatus?: string;
  addressBindingMatched?: boolean;
  latePayment?: boolean;
  linkedConsumptionOrderId?: string;
  resumeStatus?: string;
  pointLedgerTxnId?: string;
  failureCode?: string;
  failureMessage?: string;
  detectedAt?: string;
  confirmedAt?: string;
  creditedAt?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateRechargeOrderRequest {
  assetCode: RechargeAssetCode;
  amount: string;
  clientRequestId: string;
  linkedConsumptionOrderId?: string;
}

export interface PaymentAddressBinding {
  id: number;
  address: string;
  label?: string;
  status: 'ACTIVE' | 'DISABLED';
  boundAt: string;
  lastUsedAt?: string;
}

export interface BindPaymentAddressRequest {
  address: string;
  label?: string;
}

export interface RechargeClaim {
  id: number;
  claimId: string;
  assetCode: RechargeAssetCode;
  txid: string;
  rechargeOrderId?: string;
  status: 'SUBMITTED' | 'VERIFYING' | 'MATCHED' | 'CREDITED' | 'REJECTED' | 'MANUAL_REVIEW';
  actualAmount?: string;
  creditedPoints?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface SubmitRechargeClaimRequest {
  assetCode: RechargeAssetCode;
  txid: string;
  rechargeOrderId?: string;
}

export interface RechargePage<T> {
  items: T[];
  total: number;
  page?: number;
  size?: number;
}

const rechargeErrorMessages: Record<string, string> = {
  RECHARGE_DISABLED: '充值服务当前已暂停，请稍后再试。',
  RECHARGE_ASSET_DISABLED: '当前币种充值暂未开放。',
  ACCOUNT_DISABLED: '账号已禁用，暂时不能创建充值订单。',
  ACTIVE_RECHARGE_ORDER_EXISTS: '已有待付款订单，请先完成或取消当前订单。',
  // 2026-09-03 CST: Keep the client fallback aligned with the server-side limit of 10.
  ACTIVE_RECHARGE_ORDER_LIMIT: '最多只能同时保留 10 个有效充值订单。',
  RECHARGE_ORDER_COOLDOWN: '上一笔未付款订单刚刚过期，请两分钟后再试。',
  DAILY_UNPAID_ORDER_LIMIT: '今日未付款过期订单已达到上限，请明日再试。',
  RECHARGE_PROVIDER_UNAVAILABLE: '充值通道暂时繁忙，请稍后重试或联系我们。',
  ANKR_DAILY_HARD_LIMIT: '充值通道今日请求量已达到安全阈值，请联系我们。',
  RECHARGE_AMOUNT_INVALID: '请输入最多三位小数的有效充值数量。',
  RECHARGE_ORDER_NOT_FOUND: '充值订单不存在或不属于当前客户。',
  RECHARGE_ORDER_EXPIRED: '充值订单已过期，请重新创建订单。',
  PAYMENT_ADDRESS_LIMIT_REACHED: '付款地址已达到最多 10 个的限制。',
  PAYMENT_ADDRESS_OWNERSHIP_CONFLICT: '该付款地址当前已绑定其他客户，无法重复绑定。',
  PAYMENT_ADDRESS_INVALID: '请输入有效的 TRON 地址。',
  RECHARGE_CLAIM_DISABLED: '异常充值认领当前已暂停。',
  RECHARGE_DEPOSIT_NOT_FOUND: '暂未找到该链上交易，请确认交易哈希或稍后重试。',
  RECHARGE_CLAIM_IN_PROGRESS: '该交易正在核验中，请勿重复提交。',
  RECHARGE_ALREADY_CREDITED: '该链上交易已经完成积分入账。'
};

export function rechargeErrorMessage(error: unknown, fallback: string) {
  if (error instanceof ControlPlaneApiError) {
    return rechargeErrorMessages[error.code] || fallback;
  }
  return fallback;
}

export function rechargeErrorRequiresContact(error: unknown) {
  return error instanceof ControlPlaneApiError
    && ['RECHARGE_PROVIDER_UNAVAILABLE', 'ANKR_DAILY_HARD_LIMIT'].includes(error.code);
}

export function createRechargeApi(http: ConsoleHttpClient) {
  const orderPath = (orderId: string) => `/api/v1/recharge/orders/${encodeURIComponent(orderId)}`;
  return {
    config: () => http.get<RechargeConfig>('/api/v1/recharge/config'),
    createOrder: (payload: CreateRechargeOrderRequest) =>
      http.post<RechargeOrder>('/api/v1/recharge/orders', payload),
    activeOrder: () => http.get<RechargeOrder | null>('/api/v1/recharge/orders/active'),
    activeOrders: () => http.get<RechargeOrder[]>('/api/v1/recharge/orders/active-list'),
    orders: (params: Record<string, unknown>) =>
      http.get<RechargePage<RechargeOrder>>('/api/v1/recharge/orders', { params }),
    order: (orderId: string) => http.get<RechargeOrder>(orderPath(orderId)),
    cancelOrder: (orderId: string) => http.post<RechargeOrder>(`${orderPath(orderId)}/cancel`),
    paymentAddresses: () =>
      http.get<PaymentAddressBinding[]>('/api/v1/payment-addresses'),
    bindPaymentAddress: (payload: BindPaymentAddressRequest) =>
      http.post<PaymentAddressBinding>('/api/v1/payment-addresses', payload),
    unbindPaymentAddress: (id: number) =>
      http.delete<void>(`/api/v1/payment-addresses/${id}`),
    claims: (params: Record<string, unknown>) =>
      http.get<RechargePage<RechargeClaim>>('/api/v1/recharge/claims', { params }),
    submitClaim: (payload: SubmitRechargeClaimRequest) =>
      http.post<RechargeClaim>('/api/v1/recharge/claims', payload)
  };
}

export const rechargeApi = createRechargeApi(httpClient);
